import { useCallback, useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { formatCOP, formatDate, statusLabel, statusColor } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';
import { Search, X, FileDown, ChevronDown, ChevronRight, ShieldCheck, History } from 'lucide-react';
import { Link } from 'react-router-dom';
import { downloadXlsx } from '@/lib/download';
import { Pagination, usePagination } from '@/components/Pagination';

const STATUSES = [
  { v: 'all', l: 'Todos' }, { v: 'pending', l: 'Pendiente' }, { v: 'paid', l: 'Pagado' },
  { v: 'shipped', l: 'Despachado' }, { v: 'delivered', l: 'Entregado' },
  { v: 'cancelled', l: 'Cancelado' },
];

export default function AdminOrders() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ q: '', status: 'all', date_from: '', date_to: '' });
  const [expandedId, setExpandedId] = useState(null);

  useSEO({
    title: 'Pedidos | Administración',
    description: 'Todos los pedidos de la plataforma con filtros y exportación a Excel.',
    canonical: '/admin/pedidos',
  });

  const fetchData = useCallback(async () => {
    const params = {};
    if (filters.q.trim()) params.q = filters.q.trim();
    if (filters.status !== 'all') params.status = filters.status;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    const { data } = await api.get('/admin/orders', { params });
    setItems(data.items || []);
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const clearFilters = () => setFilters({ q: '', status: 'all', date_from: '', date_to: '' });
  const hasFilters = filters.q || filters.status !== 'all' || filters.date_from || filters.date_to;

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Administración</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Pedidos</h1>
          </div>
          <Button
            data-testid="export-orders-btn"
            onClick={() => {
              const p = {};
              if (filters.q.trim()) p.q = filters.q.trim();
              if (filters.status !== 'all') p.status = filters.status;
              if (filters.date_from) p.date_from = filters.date_from;
              if (filters.date_to) p.date_to = filters.date_to;
              downloadXlsx('/admin/exports/orders', 'pedidos.xlsx', p);
            }}
            variant="outline" className="rounded-none"
          >
            <FileDown className="mr-2 h-4 w-4" /> Exportar pedidos (con filtros)
          </Button>
        </div>

        <div className="mt-6 border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-[1fr,160px,160px,160px,auto] gap-3 items-end">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Buscar</label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                data-testid="admin-orders-search"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="ID, comprador, ciudad, tracking..."
                className="pl-9 rounded-none"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Estado</label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="admin-orders-status" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Desde</label>
            <Input data-testid="admin-orders-date-from" type="date" value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="rounded-none mt-1" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Hasta</label>
            <Input data-testid="admin-orders-date-to" type="date" value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="rounded-none mt-1" />
          </div>
          {hasFilters && (
            <Button variant="outline" onClick={clearFilters} className="rounded-none h-10">
              <X className="h-4 w-4 mr-1" /> Limpiar
            </Button>
          )}
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <div className="text-sm text-slate-600">{items.length} pedidos</div>
        </div>

        <PaginatedTable items={items} expandedId={expandedId} setExpandedId={setExpandedId} />
      </div>
    </Layout>
  );
}

function PaginatedTable({ items, expandedId, setExpandedId }) {
  const { pageItems, page, setPage, totalPages, total } = usePagination(items);
  return (
    <>
      <div className="mt-3 border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 w-8"></th>
                <th className="px-4 py-3">Pedido</th>
                <th className="px-4 py-3">Comprador</th>
                <th className="px-4 py-3">Items</th>
                <th className="px-4 py-3">Ciudad</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Comisión</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageItems.map((o) => {
                const isOpen = expandedId === o.order_id;
                const la = o.legal_acceptance;
                return (
                  <>
                    <tr
                      key={o.order_id}
                      className="row-hover cursor-pointer"
                      onClick={() => setExpandedId(isOpen ? null : o.order_id)}
                      data-testid={`admin-orders-row-${o.order_id}`}
                    >
                      <td className="px-4 py-3 text-slate-400">
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{o.order_id}</td>
                      <td className="px-4 py-3">{o.buyer_name}</td>
                      <td className="px-4 py-3 text-slate-500">{(o.items || []).length}</td>
                      <td className="px-4 py-3 text-slate-700">{o.shipping_city || '—'}</td>
                      <td className="px-4 py-3 text-slate-500">{formatDate(o.created_at)}</td>
                      <td className="px-4 py-3 font-bold">{formatCOP(o.total)}</td>
                      <td className="px-4 py-3 text-orange-700 font-bold">{formatCOP(o.commission_amount)}</td>
                      <td className="px-4 py-3"><Badge className={`rounded-none border ${statusColor[o.status]}`}>{statusLabel[o.status]}</Badge></td>
                      <td className="px-4 py-3">
                        <Link to={`/admin/pedidos/${o.order_id}`} onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-xs font-bold text-orange-700 hover:underline whitespace-nowrap"
                              data-testid={`admin-orders-view-detail-${o.order_id}`}>
                          <History className="h-3.5 w-3.5" /> Ver detalle
                        </Link>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${o.order_id}-detail`} className="bg-slate-50">
                        <td></td>
                        <td colSpan={9} className="px-4 py-4" data-testid={`admin-orders-detail-${o.order_id}`}>
                          <div className="border border-slate-200 bg-white p-4">
                            <div className="flex items-center gap-2 text-slate-900 font-semibold">
                              <ShieldCheck className="h-4 w-4 text-emerald-600" />
                              Aceptación legal
                              <span className="text-xs text-slate-500 font-normal">(por suborden — registro auditable)</span>
                            </div>
                            {!la || la.accepted !== true ? (
                              <div className="mt-3 text-sm text-slate-500" data-testid={`admin-orders-legal-missing-${o.order_id}`}>
                                Sin registro de aceptación legal para esta orden.
                              </div>
                            ) : (
                              <dl className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid={`admin-orders-legal-${o.order_id}`}>
                                <div className="flex gap-2">
                                  <dt className="text-slate-500 min-w-[140px]">Fecha / hora:</dt>
                                  <dd className="font-mono text-slate-900" data-testid={`admin-legal-accepted-at-${o.order_id}`}>
                                    {la.accepted_at ? new Date(la.accepted_at).toLocaleString('es-CO') : '—'}
                                  </dd>
                                </div>
                                <div className="flex gap-2">
                                  <dt className="text-slate-500 min-w-[140px]">Checkout ID:</dt>
                                  <dd className="font-mono text-xs text-slate-900" data-testid={`admin-legal-checkout-${o.order_id}`}>
                                    {la.checkout_id || '—'}
                                  </dd>
                                </div>
                                <div className="flex gap-2">
                                  <dt className="text-slate-500 min-w-[140px]">Términos:</dt>
                                  <dd className="text-slate-900" data-testid={`admin-legal-terms-version-${o.order_id}`}>
                                    {la.terms_version || '—'}
                                  </dd>
                                </div>
                                <div className="flex gap-2">
                                  <dt className="text-slate-500 min-w-[140px]">Política de Compras:</dt>
                                  <dd className="text-slate-900" data-testid={`admin-legal-policies-version-${o.order_id}`}>
                                    {la.policies_version || '—'}
                                  </dd>
                                </div>
                                <div className="flex gap-2">
                                  <dt className="text-slate-500 min-w-[140px]">IP:</dt>
                                  <dd className="font-mono text-slate-900" data-testid={`admin-legal-ip-${o.order_id}`}>
                                    {la.ip || '—'}
                                  </dd>
                                </div>
                                <div className="flex gap-2 md:col-span-2">
                                  <dt className="text-slate-500 min-w-[140px]">User-Agent:</dt>
                                  <dd className="font-mono text-xs text-slate-700 break-all" data-testid={`admin-legal-ua-${o.order_id}`}>
                                    {la.user_agent || '—'}
                                  </dd>
                                </div>
                              </dl>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
              {pageItems.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-slate-500">Sin resultados</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} total={total} setPage={setPage} testIdPrefix="admin-orders-pag" />
      </>
  );
}
